import { useQuery } from '@tanstack/react-query'
import { sessionQuery } from '@/lib/api/queries'
import { useDemo, type ConsoleRole } from '@/lib/demo/demo-context'
import { currentAccount } from '@/lib/session'

export interface ConsoleRoleInfo {
  /** The design's viewer: owner, admin or receptionist (design docs §01). */
  role: ConsoleRole
  /** Our membership role (owner, admin, staff, readonly), or undefined while loading. */
  membershipRole: string | undefined
  /** How the profile names it, e.g. "Owner", "Receptionist · view only". */
  label: string
  /** May change things. False for readonly: they see the receptionist's view, view-only. */
  canAct: boolean
  /** Billing is the owner's alone; everyone else gets "request access". */
  canSeeBilling: boolean
  /** The receptionist sees caller numbers masked (0412 ••• 884). */
  masksNumbers: boolean
}

const LABELS: Record<ConsoleRole, string> = { owner: 'Owner', admin: 'Admin', receptionist: 'Receptionist' }

function describe(role: ConsoleRole, membershipRole: string | undefined, canAct: boolean): ConsoleRoleInfo {
  return {
    role,
    membershipRole,
    label: canAct ? LABELS[role] : `${LABELS[role]} · view only`,
    canAct,
    canSeeBilling: role === 'owner',
    masksNumbers: role === 'receptionist',
  }
}

/** owner→owner, admin→admin, staff→receptionist, readonly→receptionist (view only). */
export function consoleRoleFor(membershipRole: string | undefined): ConsoleRoleInfo {
  switch (membershipRole) {
    case 'owner':
      return describe('owner', membershipRole, true)
    case 'admin':
      return describe('admin', membershipRole, true)
    case 'staff':
      return describe('receptionist', membershipRole, true)
    default:
      // readonly, an unknown role, or the session still loading: the narrowest view.
      return describe('receptionist', membershipRole, false)
  }
}

/**
 * The signed-in person as the console design sees them. The API still refuses
 * what a role may not do; this only decides what a screen offers. With the dev
 * prototype controls on, their "Viewer role" stands in.
 */
export function useConsoleRole(): ConsoleRoleInfo {
  const demo = useDemo()
  const session = useQuery(sessionQuery)
  const membershipRole = session.data ? currentAccount(session.data)?.role : undefined
  if (import.meta.env.DEV && demo.enabled) return describe(demo.role, membershipRole, true)
  return consoleRoleFor(membershipRole)
}
